import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import _, { merge } from 'lodash';
import { Button } from '@material-ui/core';
import moment from 'moment-timezone';
import { Actions } from '../../page-framework';
import { buildPageId } from '../AdminLinkUtils';
import ConfirmDeleteButton from '../../form/ConfirmDeleteButton';
import AdminPage from '../AdminPage';
import CollectorsAdminMainPanel from './CollectorsAdminMainPanel';
import { useIntervalRefreshTrigger } from '../../data-table/Triggers';
import { useFormDeleteWrapper } from '../../../services/FormDeleteService';
import {
    COLLECTOR_LIST_DATA_SOURCE,
    COLLECTORS_FUNCTION_NAME,
    ipScopeListDataSource,
    TAB_COLLECTORS,
    TAB_IPSCOPES,
} from './CollectorsAdminConstants';
import { useDataService } from '../../../services/ApiDataService';
import { buildLinkUrl } from '../../links/Link';
import useUiToasts, { createToastConfig, INFO } from '../../../stores/ToastStore';
import useAlerts from '../../../stores/AlertsStore';
import { durationBetween } from '../../../utilities/DateTimeUtilities';
import { durationSinceFormatter } from '../../../utilities/FormattingUtilities';
import CollectorDeleteDialog from './CollectorDeleteDialog';
import { usePageStateValueWithDefault } from '../../../stores/PageStateStore';

const RELOAD_SCAN_STATUS_INTERVAL = 1000 * 10;

export default function CollectorsAdminPage() {
    const { deleteWrapper } = useFormDeleteWrapper();
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();
    const [, uiToastActions] = useUiToasts();
    const [actionsDisabled, setActionsDisabled] = useState(true);
    const [tableSelection, setTableSelection] = useState(undefined);
    const [tableState, setTableState] = useState(undefined);
    // const [activeTab, setActiveTab] = useState();
    const [ipScopeScan, setIpScopeScan] = useState();
    const [collectorCanDelete, setCollectorCanDelete] = useState();
    const [selectedCollectors, setSelectedCollectors] = useState();
    const [showCollectorDeleteDialog, setShowCollectorDeleteDialog] = useState(false);
    const { triggerRef, trigger: refresher } = useIntervalRefreshTrigger();
    const [activeTab, setActiveTab] = usePageStateValueWithDefault('activeTab', TAB_COLLECTORS);

    const handleConfirmedDelete = () => {
        let dataSource;
        if (activeTab === TAB_COLLECTORS) {
            dataSource = COLLECTOR_LIST_DATA_SOURCE;
        } else if (activeTab === TAB_IPSCOPES) {
            dataSource = ipScopeListDataSource();
        }

        const params = merge(
            {
                search: tableState?.search || '',
            },
            dataSource.params
        );

        return deleteWrapper({
            deleteFunction: () => dataService.remove(`${dataSource.url}`, { selectionParams: tableSelection }, params),
            refresherFunc: refresher,
        })();
    };

    const handleRescanIpScopeSelection = () => {
        const dataSource = ipScopeListDataSource();
        const rescanUrl = '/ngp/v2/inventory/collector-scopes/scan';

        const params = merge(
            {
                search: tableState?.search || '',
                force: false,
            },
            dataSource.params
        );

        const url = buildLinkUrl({ target: rescanUrl }, null);
        dataService.post(url, { selectionParams: tableSelection }, params).then(onRescanSuccess).catch(onScanException);
    };

    const onRescanSuccess = (response) => {
        const collectorsSuccess = response.requestedCollectors === response.respondedCollectors;
        const scopeCountMatch = response.requestedCount === response.matchedCount;
        const translationKey = scopeCountMatch ? 'rescanSuccess' : 'rescanPartialSuccess';
        const SUCCESS_MESSAGE = {
            rescanSuccess: 'The selected IP scopes will be scanned',
            rescanPartialSuccess:
                'Eligible selected IP scopes will be rescanned. Exclusion and non-discovery scopes cannot be scanned.',
        };

        setScanningStatus(response);

        alertsActions.handleRemove();

        // if some of the collectors we wanted to
        if (!collectorsSuccess) {
            alertsActions.createWarningAlert(
                t`One or more collectors did not respond to the scan request. IP Scopes belonging to the unresponsive collectors might not be scanned.`
            );
        } else {
            uiToastActions.createToast(createToastConfig(INFO, SUCCESS_MESSAGE[translationKey]));
        }

        return response;
    };

    const onScanException = () => {};

    useEffect(() => {
        let scanStatusTimeoutHandle;

        const loadScanStatus = () => {
            // don't query if we're not on the ipScopes tab
            if (activeTab === TAB_IPSCOPES) {
                const scanStatusUrl = '/ngp/v2/inventory/collector-scopes/scan/status';

                const url = buildLinkUrl({ target: scanStatusUrl }, null);
                dataService
                    .get(url)
                    .then((response) => {
                        setScanningStatus(response);
                    })
                    .catch(_.noop) // ignore errors silently
                    .finally(queueScanStatus);
            }
        };

        const queueScanStatus = () => {
            clearTimeout(scanStatusTimeoutHandle);
            scanStatusTimeoutHandle = setTimeout(loadScanStatus, RELOAD_SCAN_STATUS_INTERVAL);
        };

        loadScanStatus();

        return () => {
            clearTimeout(scanStatusTimeoutHandle);
        };
    }, [activeTab, dataService]);

    /**
     * Sets the scanning status object for the UI template
     * @param {Object} scanStatus
     */
    const setScanningStatus = (scanStatus) => {
        const status = _.get(scanStatus, 'status');
        const scanStarted = _.get(scanStatus, 'scanStarted');
        const secondsToFinish = _.get(scanStatus, 'secondsToFinish');
        const userName = _.get(scanStatus, 'userName');
        const currentIpCount = _.get(scanStatus, 'currentIpCount');
        const totalIpCount = _.get(scanStatus, 'totalIpCount');

        // the case if we're not running a scan
        if (!status || !userName) {
            setIpScopeScan(null);
            return;
        }

        const now = moment.utc();
        const scanStartDate = moment(scanStarted).utc();
        const estimatedScanCompletionDate = moment(now).add(secondsToFinish, 'seconds');

        // the base case where we're "preparing" a scan. secondsToFinish will be -1
        const scan = {
            isScanning: false,
            userName,
            scanProgress: 0,
        };

        const isPending = _.includes(_.toLower(status), 'discover now scan pending');

        if (secondsToFinish > -1 && !isPending) {
            let completionDate = durationBetween(now, estimatedScanCompletionDate, true);

            if (secondsToFinish === 0) {
                // duration formatter will see 0 and replace it with '--'. That's not ideal.
                completionDate = '0m';
            }

            _.assignIn(scan, {
                isScanning: true,
                scanProgress: (currentIpCount / totalIpCount) * 100,
                elapsedTime: durationSinceFormatter(scanStartDate, { showSeconds: true }),
                completionDate,
            });
        }

        setIpScopeScan(scan);
    };

    const handleCollectorRequestedDelete = () => {
        setShowCollectorDeleteDialog(true);
    };

    return (
        <AdminPage
            title={t`Collectors`}
            pageId={buildPageId(COLLECTORS_FUNCTION_NAME)}
            actions={
                <Actions>
                    {activeTab === TAB_IPSCOPES && (
                        <>
                            <Button onClick={handleRescanIpScopeSelection} disabled={actionsDisabled}>
                                <Trans>Scan</Trans>
                            </Button>
                            <ConfirmDeleteButton
                                disabled={actionsDisabled}
                                handleConfirmedDelete={handleConfirmedDelete}
                                plural={tableSelection?.selection?.length > 1}
                            />
                        </>
                    )}
                    {activeTab === TAB_COLLECTORS && (
                        <>
                            <Button
                                onClick={handleCollectorRequestedDelete}
                                disabled={actionsDisabled || !collectorCanDelete}
                            >
                                <Trans>Delete</Trans>
                            </Button>
                            <CollectorDeleteDialog
                                open={showCollectorDeleteDialog}
                                setOpen={setShowCollectorDeleteDialog}
                                onConfirm={handleConfirmedDelete}
                                collectors={selectedCollectors}
                            />
                        </>
                    )}
                </Actions>
            }
        >
            <CollectorsAdminMainPanel
                setActionsDisabled={setActionsDisabled}
                setTableSelection={setTableSelection}
                setTableState={setTableState}
                triggerRef={triggerRef}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                ipScopeScan={ipScopeScan}
                setIpScopeScan={setIpScopeScan}
                setCollectorCanDelete={setCollectorCanDelete}
                setSelectedCollectors={setSelectedCollectors}
            />
        </AdminPage>
    );
}
